import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIcon from "@mui/icons-material/Phone";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSupWiseOT,
  fetchLocationNotOff,
  fetchOTEmpNotLeft,
} from "../../action/OTAllocatedAction";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = "#330066";

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => new Date(d).toISOString().split("T")[0];
const formatTime = (d) =>
  new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const standardMobile = (mobile) => {
  if (!mobile) return "";
  let m = mobile.toString().trim().replace(/[\s-]/g, "");
  if (m.startsWith("+94")) return "0" + m.substring(3);
  if (m.startsWith("94")) return "0" + m.substring(2);
  return m;
};

const sortByCDL = (arr) =>
  [...arr].sort((a, b) => {
    const extract = (job) => (job || "").split("/")[0].trim().toUpperCase();
    const priority = ["NC", "SR", "OR"];
    const ai = priority.indexOf(extract(a.CDLjob));
    const bi = priority.indexOf(extract(b.CDLjob));
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return extract(a.CDLjob).localeCompare(extract(b.CDLjob));
  });

// ─── Reusable badge ───────────────────────────────────────────────────────────
function Badge({ children, color = BRAND, bg = "#e8f0fe" }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "8px",
        py: "2px",
        borderRadius: "10px",
        fontSize: 11,
        fontWeight: 600,
        background: bg,
        color,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Phone button ─────────────────────────────────────────────────────────────
function CallBtn({ mobile, name }) {
  if (!mobile) return null;
  return (
    <IconButton
      size="small"
      component="a"
      href={`tel:${standardMobile(mobile)}`}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Call ${name}`}
      sx={{
        width: 26,
        height: 26,
        background: "#16a34a",
        color: "#fff",
        flexShrink: 0,
        "&:hover": { background: "#15803d" },
      }}
    >
      <PhoneIcon sx={{ fontSize: 13 }} />
    </IconButton>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL VIEW — OT Employees Not Left
// ═══════════════════════════════════════════════════════════════════════════════
function DetailView({ selectedDate, detailData, detailLoading, onBack }) {
  const sorted = detailData ? sortByCDL(detailData) : [];

  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text("Employee Overtime Engagement", pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.15, "Date – ", formatDate(selectedDate)],
      [sx + tw * 0.45, "Time – ", formatTime(at)],
      [sx + tw * 0.8, "Total – ", sorted.length.toString()],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    autoTable(doc, {
      startY: 52,
      head: [["Location", "Supervisor", "Service No", "Name", "CDL Job"]],
      body: sorted.map((i) => [
        i.Location || "",
        i.Supervisorname || "",
        i.EmployeeServiceNo || "",
        i.EmployeeName || "",
        i.CDLjob || "",
      ]),
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: {
        0: { cellWidth: 25, halign: "center" },
        1: { cellWidth: 40 },
        2: { cellWidth: 28, halign: "center" },
        3: { cellWidth: 45 },
        4: { cellWidth: 50 },
      },
      theme: "grid",
      margin: { left: 15, right: 10 },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`OT_Employees_Not_Left_${formatDate(selectedDate)}.pdf`);
  };

  return (
    <Page>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box
          onClick={onBack}
          sx={{
            width: 32, height: 32, borderRadius: "8px",
            background: "#EFF4FF", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", flexShrink: 0,
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18, color: BRAND }} />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b", flex: 1 }}>
          OT Employees Not Left
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
          onClick={handleExportPDF}
          disabled={detailLoading || !sorted.length}
          sx={{
            height: 32, borderRadius: "20px", background: "#16a34a",
            color: "#fff", fontSize: 12, fontWeight: 500,
            textTransform: "none", px: 1.5, flexShrink: 0,
            "&:hover": { background: "#15803d" },
            "&:disabled": { background: "#cbd5e1", color: "#64748b" },
          }}
        >
          PDF
        </Button>
      </Box>

      {/* ── Stats row ── */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <Box sx={{ flex: 1, background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: "12px", p: "10px 12px" }}>
          <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Date</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: BRAND }}>{formatDate(selectedDate)}</Typography>
        </Box>
        <Box sx={{ flex: 1, background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: "12px", p: "10px 12px" }}>
          <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Total Employees</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 500, color: BRAND, lineHeight: 1.1 }}>
            {sorted.length}
          </Typography>
        </Box>
      </Box>

      {/* ── Detail table ── */}
      <SectionLabel>Employee Details</SectionLabel>
      <Card>
        <TableContainer sx={{ maxHeight: "calc(100vh - 260px)", overflowX: "auto" }}>
          <Table size="small" stickyHeader sx={{ minWidth: 540 }}>
            <TableHead>
              <TableRow>
                {[
                  { label: "Location", w: "12%", align: "center" },
                  { label: "Supervisor", w: "26%", align: "left" },
                  { label: "Service No", w: "13%", align: "center" },
                  { label: "Name", w: "29%", align: "left" },
                  { label: "CDL Job", w: "20%", align: "left" },
                ].map((h) => (
                  <TableCell
                    key={h.label}
                    sx={{
                      width: h.w, background: BRAND, color: "#fff",
                      fontSize: 11, fontWeight: 600, py: "8px", px: "8px",
                      borderBottom: "none", textAlign: h.align,
                      position: "sticky", top: 0, zIndex: 10,
                    }}
                  >
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {detailLoading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </TableCell>
                </TableRow>
              ) : sorted.length > 0 ? (
                sorted.map((item, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      "&:nth-of-type(even) td": { background: "#f8faff" },
                      "&:last-child td": { borderBottom: "none" },
                    }}
                  >
                    <TableCell sx={{ textAlign: "center", px: "8px", py: "7px" }}>
                      <Badge>{item.Location}</Badge>
                    </TableCell>
                    <TableCell sx={{ px: "8px", py: "7px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, flex: 1, lineHeight: 1.3 }}>
                          {item.Supervisorname}
                        </Typography>
                        <CallBtn mobile={item.Sup_Mobile} name={item.Supervisorname} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: 12, fontWeight: 600, px: "8px", py: "7px" }}>
                      {item.EmployeeServiceNo}
                    </TableCell>
                    <TableCell sx={{ px: "8px", py: "7px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500, flex: 1, lineHeight: 1.3 }}>
                          {item.EmployeeName}
                        </Typography>
                        <CallBtn mobile={item.Emp_Mobile} name={item.EmployeeName} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: "#475569", px: "8px", py: "7px", lineHeight: 1.3 }}>
                      {item.CDLjob}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function OTAllocated() {
  const dispatch = useDispatch();

  const { loading: supLoading, data: supData } = useSelector((s) => s.otAllocated.supervisorWiseOT);
  const { loading: locLoading, data: locData } = useSelector((s) => s.otAllocated.locationNotOff);
  const { loading: detailLoading, data: detailData } = useSelector((s) => s.otAllocated.otEmpNotLeft);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("supervisor");
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
    const fd = formatDate(today);
    viewMode === "supervisor" ? dispatch(fetchSupWiseOT(fd)) : dispatch(fetchLocationNotOff(fd));
  }, [dispatch, viewMode]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const fd = formatDate(date);
    viewMode === "supervisor" ? dispatch(fetchSupWiseOT(fd)) : dispatch(fetchLocationNotOff(fd));
  };

  const handleViewModeChange = (e) => {
    setViewMode(e.target.value);
    setShowDetail(false);
  };

  const handleNotLeftClick = () => {
    setShowDetail(true);
    dispatch(fetchOTEmpNotLeft(formatDate(selectedDate)));
  };

  const currentData = viewMode === "supervisor" ? supData : locData;
  const loading = viewMode === "supervisor" ? supLoading : locLoading;

  const countKey = viewMode === "supervisor" ? "Sup_totalCount" : "Loc_totalCount";
  const empCountKey = viewMode === "supervisor" ? "Sup_empCount" : "Loc_empCount";
  const assignCount = currentData?.reduce((s, i) => s + Number(i[countKey] || 0), 0) || 0;
  const notLeftCount = currentData?.reduce((s, i) => s + Number(i[empCountKey] || 0), 0) || 0;

  // ── PDF exports ───────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();
    const isSup = viewMode === "supervisor";

    const title = isSup
      ? "OT Allocated Employees – Supervisor Wise"
      : "OT Allocated Employees – Location Wise";

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(30, 58, 110);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text(title, pw / 2, 30, { align: "center" });
    doc.setDrawColor(30, 58, 110).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const y = 44, sx = 15, tw = pw - 30;
    [
      [sx + tw * 0.125, "Date – ", formatDate(selectedDate)],
      [sx + tw * 0.375, "Time – ", formatTime(at)],
      [sx + tw * 0.625, "Assigned – ", assignCount.toString()],
      [sx + tw * 0.875, "Not Left – ", notLeftCount.toString()],
    ].forEach(([col, bold, val]) => {
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, y);
      doc.setFont("helvetica", "normal").text(val, col - fw / 2 + doc.getTextWidth(bold), y);
    });

    const columns = isSup
      ? ["Supervisor Name", "Total Assign", "Not Left"]
      : ["Location", "Total Assign", "Not Left"];

    const rows = (currentData || []).map((item) =>
      isSup
        ? [item.Sup_name || "", item.Sup_totalCount || "", item.Sup_empCount || ""]
        : [item.Loc_location || "", item.Loc_totalCount || "", item.Loc_empCount || ""]
    );
    rows.push(["TOTAL", assignCount.toString(), notLeftCount.toString()]);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 52,
      styles: { fontSize: 12, cellPadding: 5 },
      headStyles: { fillColor: [30, 58, 110], textColor: 255, fontStyle: "bold", halign: "center" },
      alternateRowStyles: { fillColor: [248, 249, 252] },
      columnStyles: isSup
        ? { 0: { cellWidth: 107 }, 1: { cellWidth: 40, halign: "center" }, 2: { cellWidth: 40, halign: "center" } }
        : { 0: { cellWidth: 40, halign: "center" }, 1: { cellWidth: 40, halign: "center" }, 2: { cellWidth: 40, halign: "center" } },
      theme: "grid",
      margin: { left: 12, right: 13 },
      didParseCell(data) {
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [21, 76, 121];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pw - 20, doc.internal.pageSize.getHeight() - 10);
      },
    });

    doc.save(`OT_${isSup ? "Supervisor" : "Location"}_${formatDate(selectedDate)}.pdf`);
  };

  // ── Detail view ───────────────────────────────────────────────────────────
  if (showDetail) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DetailView
          selectedDate={selectedDate}
          detailData={detailData}
          detailLoading={detailLoading}
          onBack={() => setShowDetail(false)}
        />
      </LocalizationProvider>
    );
  }

  // ── Summary view ──────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {/* View mode select */}
          <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
            <Select
              value={viewMode}
              onChange={handleViewModeChange}
              sx={{
                height: 36, borderRadius: "20px", background: "#fff",
                fontSize: 12, fontWeight: 500, color: "#1e293b",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              <MenuItem value="supervisor" sx={{ fontSize: 13 }}>By Supervisor</MenuItem>
              <MenuItem value="location" sx={{ fontSize: 13 }}>By Location</MenuItem>
            </Select>
          </FormControl>

          {/* Date pill */}
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36, borderRadius: "20px", border: "0.5px solid #e2e8f0",
              background: "#fff", display: "flex", alignItems: "center",
              gap: "6px", px: "12px", cursor: "pointer", flexShrink: 0,
              "&:hover": { borderColor: BRAND },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={handleDateChange}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
            />
          )}
        </Box>

        {/* ── Stats row ── */}
        {!loading && currentData?.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            {/* Assigned count */}
            <Box sx={{ flex: 1, background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: "12px", p: "10px 12px" }}>
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>Assigned</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 500, color: BRAND, lineHeight: 1.1 }}>
                {assignCount}
              </Typography>
            </Box>

            {/* Not left — tappable */}
            <Box
              onClick={() => setTimeout(handleNotLeftClick, 150)}
              sx={{
                flex: 1, background: "#fff", border: "0.5px solid #e2e8f0",
                borderRadius: "12px", p: "10px 12px", cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  background: "#EFF4FF", borderColor: BRAND,
                  "& .nl-val": { color: BRAND },
                },
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
                Not Left 
              </Typography>
              <Typography className="nl-val" sx={{ fontSize: 22, fontWeight: 500, color: "#dc2626", lineHeight: 1.1, transition: "color 0.15s" }}>
                {notLeftCount}
              </Typography>
            </Box>

            {/* PDF button */}
            <Button
              size="small"
              // startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportPDF}
              disabled={loading || !currentData?.length}
              sx={{
                minWidth: 0, px: "12px", borderRadius: "12px",
                background: "#16a34a", color: "#fff", fontSize: 11,
                fontWeight: 500, textTransform: "none", flexShrink: 0,
                flexDirection: "column", gap: "1px",
                "&:hover": { background: "#15803d" },
                "&:disabled": { background: "#cbd5e1", color: "#64748b" },
              }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
              <Box component="span" sx={{ fontSize: 10 }}>PDF</Box>
            </Button>
          </Box>
        )}

        {/* ── Table ── */}
        <SectionLabel>
          {viewMode === "supervisor" ? "Supervisor wise OT" : "Location wise OT"}
        </SectionLabel>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: BRAND }} />
          </Box>
        ) : (
          <Card>
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: viewMode === "supervisor" ? "60%" : "40%",
                      background: BRAND, color: "#fff", fontSize: 11,
                      fontWeight: 600, py: "8px", px: "10px",
                      borderBottom: "none",
                      textAlign: viewMode === "supervisor" ? "left" : "center",
                      position: "sticky", top: 0, zIndex: 10,
                    }}
                  >
                    {viewMode === "supervisor" ? "Supervisor Name" : "Location"}
                  </TableCell>
                  {["Total Assign", "Not Left"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        width: viewMode === "supervisor" ? "20%" : "30%",
                        background: BRAND, color: "#fff", fontSize: 11,
                        fontWeight: 600, py: "8px", px: "6px",
                        borderBottom: "none", textAlign: "center",
                        position: "sticky", top: 0, zIndex: 10,
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {currentData?.length > 0 ? (
                  currentData.map((item, i) => {
                    const isSup = viewMode === "supervisor";
                    const name = isSup ? item.Sup_name : item.Loc_location;
                    const total = isSup ? item.Sup_totalCount : item.Loc_totalCount;
                    const notLeft = isSup ? item.Sup_empCount : item.Loc_empCount;
                    const notLeftNum = Number(notLeft || 0);
                    return (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(21,76,121,0.04)", cursor: "pointer" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: 12, px: "10px", py: "8px",
                            fontWeight: isSup ? 500 : 400,
                            textAlign: isSup ? "left" : "center",
                            whiteSpace: "normal", wordBreak: "break-word",
                          }}
                        >
                          {isSup ? (
                            name
                          ) : (
                            <Badge>{name}</Badge>
                          )}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Badge>{total}</Badge>
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Badge
                            color={notLeftNum > 0 ? "#b91c1c" : "#15803d"}
                            bg={notLeftNum > 0 ? "#fee2e2" : "#dcfce7"}
                          >
                            {notLeft}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "center", py: 6, fontSize: 13, color: "#94a3b8" }}>
                      No records found
                    </TableCell>
                  </TableRow>
                )}

                {/* Total row */}
                {currentData?.length > 0 && (
                  <TableRow sx={{ "& td": { background: "#EFF4FF !important", borderBottom: "none" } }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600, color: BRAND, px: "10px", py: "8px" }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block", px: "8px", py: "2px",
                          borderRadius: "10px", fontSize: 11, fontWeight: 600,
                          background: "#fff", color: BRAND, border: "0.5px solid #c7d7f8",
                        }}
                      >
                        {assignCount}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block", px: "8px", py: "2px",
                          borderRadius: "10px", fontSize: 11, fontWeight: 600,
                          background: "#fff",
                          color: notLeftCount > 0 ? "#b91c1c" : "#15803d",
                          border: `0.5px solid ${notLeftCount > 0 ? "#fecaca" : "#bbf7d0"}`,
                        }}
                      >
                        {notLeftCount}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        )}
      </Page>
    </LocalizationProvider>
  );
}